/**
 * The Analytics screen's "Approval cards" table arithmetic (owner roadmap 2026-10-08, P2.21): the service's counts per
 * system and tool (GET /v1/admin/analytics `approvals`, services/gyro-svc/src/approvalAnalytics.ts) with the shares and
 * the time to decide in plain words, and the one-line note that makes a card kind stand out: always confirmed within a
 * second (a card nobody reads), or often cancelled or rejected (a card people do not want).
 *
 * Each card counts once, by where it stands now: confirmed, cancelled, rejected, awaiting an approver, or with no
 * decision at all. "No decision" is a card still open, expired unanswered (the service writes no row for that, so it
 * is counted as a proposal with no deciding row) or ended by a tool setting. The time to decide is the person's own
 * decision (confirm, submit for approval or cancel) from the proposal; an approver's later decision is not timed.
 */
import type { ApprovalCardRow } from './api';

/** A note needs this many decisions before it says anything about a card kind. */
export const APPROVAL_NOTE_MIN_DECIDED = 5;
/** A median at or under this many seconds, with every decision a confirmation, is "confirmed at a glance". */
export const APPROVAL_GLANCE_SECONDS = 1;
/** Cancelled or rejected at or above this share of decisions is "often turned down". */
export const APPROVAL_TURNED_DOWN_SHARE = 0.5;

/** What the "No decision" column holds, as the table head says it. */
export const APPROVAL_UNDECIDED_LABEL = 'No decision (open, expired or blocked)';

export interface ApprovalCardView {
  app: string;
  tool: string;
  proposed: number;
  confirmed: number;
  cancelled: number;
  rejected: number;
  awaiting: number;
  undecided: number;
  /** Confirmed plus cancelled plus rejected: the cards somebody finally decided. */
  decided: number;
  /** Confirmed as a share of the decisions, 0 to 100; null with no decision. */
  confirmedShare: number | null;
  /** The median time to decide in words ("1.5 s", "2 min 5 s", "3 h 4 min"), or "—" with none timed. */
  medianText: string;
  /** What stands out about this card kind, or an empty string. */
  note: string;
}

/** Seconds in words: under a minute to one decimal, then minutes and seconds, then hours and minutes. */
export function secondsText(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return '—';
  if (seconds < 60) return `${Math.round(seconds * 10) / 10} s`;
  const whole = Math.round(seconds);
  if (whole < 3_600) return `${Math.floor(whole / 60)} min ${whole % 60} s`;
  return `${Math.floor(whole / 3_600)} h ${Math.floor((whole % 3_600) / 60)} min`;
}

/** The note for one row, or an empty string: it never speaks from fewer than APPROVAL_NOTE_MIN_DECIDED decisions. */
export function approvalNote(row: ApprovalCardRow): string {
  const decided = row.confirmed + row.cancelled + row.rejected;
  if (decided < APPROVAL_NOTE_MIN_DECIDED) return '';
  const turnedDown = (row.cancelled + row.rejected) / decided;
  if (turnedDown >= APPROVAL_TURNED_DOWN_SHARE) return `Often turned down: ${Math.round(turnedDown * 100)}% of decisions cancel or reject it.`;
  if (row.confirmed === decided && row.medianSecondsToDecision !== null && row.timed >= APPROVAL_NOTE_MIN_DECIDED && row.medianSecondsToDecision <= APPROVAL_GLANCE_SECONDS) {
    return 'Always confirmed within a second: people may not be reading this card.';
  }
  return '';
}

/** The table's rows, as the service ordered them (highest proposed first); empty for a service without the figures. */
export function approvalCardViews(rows: ApprovalCardRow[] | undefined): ApprovalCardView[] {
  return (rows ?? []).map((row) => {
    const decided = row.confirmed + row.cancelled + row.rejected;
    return {
      app: row.app,
      tool: row.tool,
      proposed: row.proposed,
      confirmed: row.confirmed,
      cancelled: row.cancelled,
      rejected: row.rejected,
      awaiting: row.awaiting,
      undecided: row.undecided,
      decided,
      confirmedShare: decided ? Math.round((row.confirmed / decided) * 100) : null,
      medianText: secondsText(row.medianSecondsToDecision),
      note: approvalNote(row),
    };
  });
}

/** One line for the card's head. */
export function approvalHeadline(rows: ApprovalCardRow[] | undefined, days: number): string {
  if (!rows) return 'Not reported by this service version.';
  const sum = (pick: (row: ApprovalCardRow) => number) => rows.reduce((total, row) => total + pick(row), 0);
  const proposed = sum((row) => row.proposed);
  if (!proposed) return `No approval cards in the last ${days} days.`;
  const decided = sum((row) => row.confirmed + row.cancelled + row.rejected);
  const confirmed = sum((row) => row.confirmed);
  const awaiting = sum((row) => row.awaiting);
  const undecided = sum((row) => row.undecided);
  const share = decided ? ` ${Math.round((confirmed / decided) * 100)}% of decisions confirmed.` : '';
  const pending = [awaiting ? `${awaiting.toLocaleString('en-US')} awaiting an approver` : '', undecided ? `${undecided.toLocaleString('en-US')} with no decision` : ''].filter(Boolean).join(', ');
  return `${proposed.toLocaleString('en-US')} card${proposed === 1 ? '' : 's'} proposed in the last ${days} days, ${decided.toLocaleString('en-US')} decided${pending ? `, ${pending}` : ''}.${share}`;
}
